// =============================================================================
// Estúdio de Criação — uma campanha aberta, com as quatro abas:
//   Identidade · Stories · Pôster A3 · Catálogo
// A identidade (logo e fundo da campanha) sobe uma vez e vale nas três peças;
// trocar a arte lá avisa as abas abertas (`versaoArte`) pra elas redesenharem.
// As abas ficam montadas depois de abertas (só escondidas): trocar de aba não
// perde o que está sendo montado. Cada aba conta o que tem e se há mudança não
// salva (`onResumo`); sair com mudança não salva pergunta antes.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import Tela, { Painel } from '../components/estudio/Tela';
import Identidade from '../components/estudio/Identidade';
import CampanhaStory from './CampanhaStory';
import CampanhaPoster from './CampanhaPoster';
import CampanhaCatalogo from './CampanhaCatalogo';
import { ABAS_ESTUDIO, BASE_ESTUDIO, lembrarCampanha, linkEstudio } from '../utils/estudio';

const dia = (d) => (d ? String(d).slice(0, 10).split('-').reverse().slice(0, 2).join('/') : '');

export default function EstudioCampanha() {
  const { id } = useParams();
  // Trocar de campanha remonta tudo do zero (as abas são da campanha).
  return <Campanha key={id} id={id} />;
}

function Campanha({ id }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [campanha, setCampanha] = useState(null);
  const [resumoInicial, setResumoInicial] = useState(null);   // contagens salvas (antes de abrir a aba)
  const [outras, setOutras] = useState([]);
  const [resumo, setResumo] = useState({});                   // aba -> { qtd, sujo }
  const [versaoArte, setVersaoArte] = useState(0);
  const [abertas, setAbertas] = useState(() => new Set());
  const pedida = params.get('aba');
  const aba = ABAS_ESTUDIO.includes(pedida) ? pedida : null;

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/campanhas/${id}`).then(r => { if (vivo) { setCampanha(r.data); lembrarCampanha(r.data); } })
      .catch(() => { if (vivo) setCampanha({ id, nome: `Campanha ${id}` }); });
    axios.get(`${API}/catalogo/estudio/campanhas`).then(r => {
      if (!vivo) return;
      setOutras(r.data || []);
      const c = (r.data || []).find(x => String(x.id) === String(id));
      setResumoInicial(c || { artes: [], stories: 0, posters: 0, catalogo: 0 });
    }).catch(() => { if (vivo) setResumoInicial({ artes: [], stories: 0, posters: 0, catalogo: 0 }); });
    return () => { vivo = false; };
  }, [id]);

  // Sem aba no endereço: campanha sem identidade começa por ela; o resto, pelos stories.
  useEffect(() => {
    if (aba || !resumoInicial) return;
    setParams({ aba: resumoInicial.artes.length ? 'stories' : 'identidade' }, { replace: true });
  }, [aba, resumoInicial, setParams]);

  useEffect(() => { if (aba) setAbertas(prev => (prev.has(aba) ? prev : new Set([...prev, aba]))); }, [aba]);

  const irPara = useCallback((a) => setParams({ aba: a }), [setParams]);
  const relatar = useCallback((qual) => (r) => setResumo(prev => (
    prev[qual]?.qtd === r.qtd && prev[qual]?.sujo === r.sujo ? prev : { ...prev, [qual]: r })), []);
  const relatores = useMemo(() => Object.fromEntries(ABAS_ESTUDIO.map(a => [a, relatar(a)])), [relatar]);
  const mudouArte = useCallback(() => setVersaoArte(v => v + 1), []);

  const sujas = ABAS_ESTUDIO.filter(a => resumo[a]?.sujo);
  // Fechar a aba do navegador com mudança não salva: o navegador pergunta.
  useEffect(() => {
    if (!sujas.length) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [sujas.length]);

  const sair = async (destino) => {
    if (sujas.length) {
      const nomes = { stories: 'Stories', poster: 'Pôster', catalogo: 'Catálogo' };
      const r = await Swal.fire({ icon: 'warning', title: 'Sair sem salvar?',
        text: `Tem mudança não salva em: ${sujas.map(a => nomes[a] || a).join(', ')}.`,
        showCancelButton: true, confirmButtonText: 'Sair mesmo assim', cancelButtonText: 'Continuar aqui' });
      if (!r.isConfirmed) return;
    }
    navigate(destino);
  };

  const qtd = (a, salvo) => (resumo[a] ? resumo[a].qtd : salvo);
  const ini = resumoInicial || {};
  const nArtes = resumo.identidade ? resumo.identidade.qtd : (ini.artes || []).length;
  const ponto = (a) => (resumo[a]?.sujo ? ' •' : '');
  const abas = [
    { id: 'identidade', rotulo: 'Identidade', icone: 'palette', qtd: nArtes || '!', alerta: !nArtes,
      dica: 'Logo e fundo da campanha: sobe uma vez e vale nas três peças' },
    { id: 'stories', rotulo: `Stories${ponto('stories')}`, icone: 'instagram', qtd: qtd('stories', ini.stories),
      dica: 'Stories do Instagram (1080 × 1920) com 1 a 3 produtos' },
    { id: 'poster', rotulo: `Pôster A3${ponto('poster')}`, icone: 'file-earmark-image', qtd: qtd('poster', ini.posters),
      dica: 'Pôster A3 de oferta, um produto por página' },
    { id: 'catalogo', rotulo: `Catálogo${ponto('catalogo')}`, icone: 'journal-richtext', qtd: qtd('catalogo', ini.catalogo),
      dica: 'Catálogo de ofertas em PDF (A4)' },
  ];

  const sub = campanha ? [campanha.data_inicio ? `${dia(campanha.data_inicio)} a ${dia(campanha.data_fim)}` : null, campanha.status].filter(Boolean).join(' · ') : '';
  return (
    <Tela titulo={campanha?.nome || 'Carregando...'} sub={sub}
      voltar={{ dica: 'Voltar pras campanhas do estúdio', onClick: () => sair(BASE_ESTUDIO) }}
      abas={abas} aba={aba} onAba={irPara}
      acoes={<>
        <select className="form-select form-select-sm" style={{ width: 210 }} value={id} title="Trocar de campanha"
          onChange={e => sair(linkEstudio(e.target.value, aba))}>
          {!outras.some(c => String(c.id) === String(id)) && <option value={id}>{campanha?.nome || id}</option>}
          {outras.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <button type="button" className="btn btn-sm btn-outline-secondary" style={{ whiteSpace: 'nowrap' }}
          onClick={() => sair(`/campanhas/${id}/produtos`)} title="Abrir os produtos e preços da campanha">
          <i className="bi bi-box-seam me-1"></i>Produtos
        </button>
      </>}>
      <Painel ativo={aba === 'identidade'}>
        {abertas.has('identidade') && <Identidade campanhaId={id} onMudou={mudouArte} onResumo={relatores.identidade} />}
      </Painel>
      <Painel ativo={aba === 'stories'}>
        {abertas.has('stories') && <CampanhaStory embutido campanhaId={id} versaoArte={versaoArte}
          onResumo={relatores.stories} onIdentidade={() => irPara('identidade')} />}
      </Painel>
      <Painel ativo={aba === 'poster'}>
        {abertas.has('poster') && <CampanhaPoster embutido campanhaId={id} versaoArte={versaoArte}
          onResumo={relatores.poster} onIdentidade={() => irPara('identidade')} />}
      </Painel>
      <Painel ativo={aba === 'catalogo'}>
        {abertas.has('catalogo') && <CampanhaCatalogo embutido campanhaId={id} versaoArte={versaoArte}
          onResumo={relatores.catalogo} onIdentidade={() => irPara('identidade')} />}
      </Painel>
    </Tela>
  );
}
